import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, TrendingUp, AlertTriangle, Shield, DollarSign, BarChart3, Loader2, ChevronDown } from 'lucide-react';
// Api base comes from env; endpoint path is fixed per request
import { useTheme } from '../components/ThemeContext';
import { SEOMeta, getToolSEOMeta } from '../components/SEOMeta';
import Analytics from '../services/analytics';

interface StockData {
  id: string;
  symbol: string;
  primaryType: string;
  sector: string;
  industry: string;
  marketCap: number;
  grade: string;
  overallScore: number;
  qualityFlags: string[];
  warnings: string[];
  description: string;
  fundamentalChecks?: {
    dividendYield?: number;
    isProfitable?: boolean;
  };
  stockSummary?: {
    grade?: string;
    primaryType?: string;
    qualityFlags?: string[];
  };
}

interface FilterState {
  primaryType: string;
  minMarketCap: string;
  maxMarketCap: string;
  grade: string;
  qualityFlag: string;
}

const Screener: React.FC = () => {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState<StockData[]>([]);
  const [filteredStocks, setFilteredStocks] = useState<StockData[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Server-driven controls
  type ScreenerMode = 'topOverall' | 'topByCategory' | 'strongFundamentals' | 'turnaroundCandidates';
  const [mode, setMode] = useState<ScreenerMode>('topByCategory');
  const [category, setCategory] = useState<string>('AT_RISK'); // default Top At Risk
  const [serverSector, setServerSector] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(50);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const fetchingRef = useRef<boolean>(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const { theme } = useTheme();
  const [symbolSearch, setSymbolSearch] = useState('');
  const [searchingSymbol, setSearchingSymbol] = useState(false);
  const [symbolSearchMode, setSymbolSearchMode] = useState(false);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Fetch individual stock by symbol
  const fetchStockBySymbol = async (symbol: string) => {
    if (!symbol.trim()) return;

    setSearchingSymbol(true);
    setSymbolSearchMode(true);
    try {
      const token = sessionStorage.getItem('STOCK_AUTH_TOKEN') ?? '';
      const base = (import.meta.env.VITE_APP_API_URL as string) || 'http://localhost:5000';
      const url = `${base}/api/screener/stockClassification/${symbol.toUpperCase().trim()}`;

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        }
      });

      if (response.ok) {
        const stockData = await response.json();

        // Normalize the data structure
        const normalizedStock = {
          id: stockData.id || stockData.symbol,
          symbol: stockData.symbol,
          primaryType: stockData.primaryType || stockData.stockSummary?.primaryType || '',
          sector: stockData.sector || '',
          industry: stockData.industry || '',
          marketCap: stockData.marketCap || 0,
          grade: stockData.grade || stockData.stockSummary?.grade?.match(/[A-F]/i)?.[0]?.toUpperCase() || '',
          overallScore: stockData.overallScore || stockData.fundamentalChecks?.overallScoreOn5Scale || 0,
          qualityFlags: stockData.qualityFlags || stockData.fundamentalChecks?.qualityFlags || stockData.stockSummary?.qualityFlags || [],
          warnings: stockData.warnings || stockData.fundamentalChecks?.warnings || [],
          description: stockData.description || stockData.stockSummary?.summary || '',
          fundamentalChecks: stockData.fundamentalChecks,
          stockSummary: stockData.stockSummary
        };

        // Set the single stock result
        setStocks([normalizedStock]);
        setFilteredStocks([normalizedStock]);
        setTotalCount(1);
        setHasMore(false);

        // Auto-select the appropriate category tab based on primaryType
        const typeToCategory: Record<string, PresetKind> = {
          'At Risk': 'AT_RISK',
          'High Growth': 'GROWTH',
          'Value': 'VALUE',
          'Dividend': 'DIVIDEND',
          'Turnaround': 'TURNAROUND'
        };

        const matchedCategory = typeToCategory[normalizedStock.primaryType];
        if (matchedCategory) {
          setCategory(matchedCategory);
          setMode('topByCategory');
        }

        // Compute options for the single result
        computeOptions([normalizedStock]);
      } else {
        console.error(`Failed to fetch stock ${symbol}: ${response.status}`);
        alert(`Stock "${symbol}" not found or error occurred.`);
        // Clear results on error
        setStocks([]);
        setFilteredStocks([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching stock by symbol:', error);
      alert(`Error searching for stock "${symbol}".`);
      // Clear results on error
      setStocks([]);
      setFilteredStocks([]);
      setTotalCount(0);
    } finally {
      setSearchingSymbol(false);
    }
  };

  // Handle symbol search submission
  const handleSymbolSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (symbolSearch.trim()) {
      Analytics.event('screener_symbol_search', { symbol: symbolSearch.trim().toUpperCase() });
      fetchStockBySymbol(symbolSearch.trim());
    }
  };

  // Clear symbol search and return to normal mode
  const clearSymbolSearch = () => {
    setSymbolSearch('');
    setSymbolSearchMode(false);
    setPage(0);
    fetchScreenerData(false, 0);
  };

  // Quick presets handler (tabs)
  type PresetKind = 'AT_RISK' | 'GROWTH' | 'VALUE' | 'DIVIDEND' | 'TURNAROUND';
  const applyPreset = (kind: PresetKind) => {
    // Clear symbol search mode when switching presets
    setSymbolSearch('');
    setSymbolSearchMode(false);
    setMode('topByCategory');
    setCategory(kind);
    Analytics.event('screener_preset', { preset: kind });
    setPage(0);
    // clear client-side refinements so presets show full server result
    setFilters(prev => ({ ...prev, primaryType: '', grade: '', qualityFlag: '' }));
  };

  const [filters, setFilters] = useState<FilterState>({
    primaryType: '',
    minMarketCap: '',
    maxMarketCap: '',
    grade: '',
    qualityFlag: ''
  });
  // Dynamic option sets derived from fetched data
  const [primaryTypeOptions, setPrimaryTypeOptions] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'All Types' },
  ]);
  const [marketCapOptions, setMarketCapOptions] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'Any' },
  ]);
  const [gradeOptions, setGradeOptions] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'All Grades' },
  ]);
  const [qualityFlagOptions, setQualityFlagOptions] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'All Flags' },
  ]);
  const [sectorOptions, setSectorOptions] = useState<string[]>([]);

  // Helpers to normalize/derive fields
  const extractPrimaryType = (s: any): string => (s?.primaryType || s?.stockSummary?.primaryType || '') as string;
  const numericMarketCap = (s: any): number => {
    const v: any = s?.marketCap;
    const n = typeof v === 'number' ? v : Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  };
  const extractGradeLetter = (s: any): string => {
    const g = (s?.stockSummary?.grade || '') as string;
    const m = g.match(/[A-F]/i);
    const letter = m ? m[0].toUpperCase() : '';
    return ['A', 'B', 'C', 'D', 'F'].includes(letter) ? letter : '';
  };
  const extractQualityFlags = (s: any): string[] => {
    // Try multiple shapes: stockSummary.qualityFlags, top-level qualityFlags, string, array of strings, or array of objects
    let raw: any = s?.stockSummary?.qualityFlags ?? s?.qualityFlags ?? [];
    if (!raw) return [];
    if (typeof raw === 'string') {
      raw = raw.split(/[,;|]/).map((x: string) => x.trim()).filter(Boolean);
    }
    if (Array.isArray(raw)) {
      const vals = raw.map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') return item.name || item.label || '';
        return '';
      }).filter(Boolean);
      return Array.from(new Set(vals));
    }
    return [];
  };
  const MARKET_BUCKETS = [
    { value: '0-300000000', label: 'Micro Cap (< $300M)', min: 0, max: 300_000_000 },
    { value: '300000000-2000000000', label: 'Small Cap ($300M - $2B)', min: 300_000_000, max: 2_000_000_000 },
    { value: '2000000000-10000000000', label: 'Mid Cap ($2B - $10B)', min: 2_000_000_000, max: 10_000_000_000 },
    { value: '10000000000-200000000000', label: 'Large Cap ($10B - $200B)', min: 10_000_000_000, max: 200_000_000_000 },
    { value: '200000000000-9999999999999', label: 'Mega Cap (> $200B)', min: 200_000_000_000, max: 9_999_999_999_999 },
  ] as const;
  const computeOptions = (data: any[]) => {
    const pSet = new Set<string>();
    const gSet = new Set<string>();
    const qSet = new Set<string>();

    data.forEach((d) => {
      const pt = extractPrimaryType(d); if (pt) pSet.add(pt);
      const gl = extractGradeLetter(d); if (gl) gSet.add(gl);
      extractQualityFlags(d).forEach(f => qSet.add(f));
    });

    setPrimaryTypeOptions([{ value: '', label: 'All Types' }, ...Array.from(pSet).sort().map(v => ({ value: v, label: v }))]);
    setGradeOptions([{ value: '', label: 'All Grades' }, ...Array.from(gSet).sort().map(v => ({ value: v, label: v }))]);
    setQualityFlagOptions([{ value: '', label: 'All Flags' }, ...Array.from(qSet).sort().map(v => ({ value: v, label: v }))]);

    // Show fixed set of market-cap buckets (<= $200B) regardless of current page composition
    setMarketCapOptions([{ value: '', label: 'Any' }, ...MARKET_BUCKETS.map(b => ({ value: b.value, label: b.label }))]);
  };

  // Fetch sectors metadata
  const fetchSectors = async () => {
    try {
      const base = (import.meta.env.VITE_APP_API_URL as string) || 'http://localhost:5000';
      const url = `${base}/api/metadata/sectors`;
      const res = await fetch(url);
      if (res.ok) {
        const list = await res.json();
        if (Array.isArray(list)) setSectorOptions(list.filter(Boolean).sort());
      }
    } catch (e) {
      console.error('Failed to load sectors metadata', e);
    }
  };

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchScreenerData = useCallback(async (isLoadMore = false, fetchPage = page) => {
    // Prevent multiple simultaneous requests
    if (fetchingRef.current) return;

    fetchingRef.current = true;
    if (!isLoadMore) setLoading(true); else setLoadingMore(true);
    try {
      const token = sessionStorage.getItem('STOCK_AUTH_TOKEN') ?? '';
      const base = (import.meta.env.VITE_APP_API_URL as string) || 'http://localhost:5000';
      const params = new URLSearchParams();
      params.set('mode', mode);
      if (mode === 'topByCategory' && category) params.set('category', category);
      if (serverSector) params.set('sector', serverSector);
      // Use 0-based pagination (matches previous working behavior)
      params.set('page', String(fetchPage));
      params.set('size', String(size));
      const url = `${base}/api/screener/stockClassification?${params.toString()}`;

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        }
      });

      if (response.ok) {
        // Prefer body.total + body.items, fallback to header and raw array body
        const body = await response.json();

        // Try to read total from body
        if (body && typeof body.total === 'number') {
          setTotalCount(body.total);
        } else {
          // Fallback: read total count header if provided; try multiple header names
          const headerNames = ['X-Total-Count', 'x-total-count', 'X-Total', 'x-total', 'Total-Count', 'total-count'];
          let totalHeader: string | null = null;
          for (const name of headerNames) {
            const v = response.headers.get(name);
            if (v) { totalHeader = v; break; }
          }
          if (totalHeader && !Number.isNaN(Number(totalHeader))) {
            setTotalCount(Number(totalHeader));
          } else {
            console.warn('Total count not provided in body.total or exposed via headers.');
          }
        }

        // Extract items
        // Extract items (support multiple common fields)
        const items: any[] = Array.isArray(body)
          ? body
          : Array.isArray(body?.items) ? body.items
            : Array.isArray(body?.data) ? body.data
              : Array.isArray(body?.content) ? body.content
                : Array.isArray(body?.results) ? body.results
                  : Array.isArray(body?.rows) ? body.rows
                    : Array.isArray(body?.page?.content) ? body.page.content
                      : [];

        // append or replace aggregate list and recompute options atomically
        setStocks(prev => {
          const combined = isLoadMore ? [...prev, ...items] : items;
          computeOptions(combined);
          // Update hasMore based on the combined length
          if (typeof body?.total === 'number') {
            setHasMore(combined.length < body.total);
          } else {
            setHasMore(Array.isArray(items) && items.length === size);
          }
          return combined;
        });

        // Set total count if available
        if (body && typeof body.total === 'number') {
          setTotalCount(body.total);
        } else {
          // Fallback: read total count header if provided
          const headerNames = ['X-Total-Count', 'x-total-count', 'X-Total', 'x-total', 'Total-Count', 'total-count'];
          let totalHeader: string | null = null;
          for (const name of headerNames) {
            const v = response.headers.get(name);
            if (v) { totalHeader = v; break; }
          }
          if (totalHeader && !Number.isNaN(Number(totalHeader))) {
            setTotalCount(Number(totalHeader));
          } else {
            console.warn('Total count not provided in body.total or exposed via headers.');
          }
        }
      } else {
        console.error('Failed to fetch screener data');
      }
    } catch (error) {
      console.error('Error fetching screener data:', error);
    } finally {
      if (!isLoadMore) setLoading(false); else setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [mode, category, serverSector, size]);

  // Reset list when server parameters change (new query)
  useEffect(() => {
    // Don't fetch if we're in symbol search mode
    if (symbolSearchMode) return;

    fetchingRef.current = false; // Reset fetch lock
    setStocks([]);
    setFilteredStocks([]);
    setHasMore(true);
    setPage(0);
    setTotalCount(null);
    // Immediately fetch first page for current controls
    fetchScreenerData(false, 0);
  }, [mode, serverSector, size, category]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stocks, filters, searchTerm]);

  // Infinite scroll observer
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore && !fetchingRef.current) {
        setPage((p) => {
          const next = p + 1;
          fetchScreenerData(true, next);
          return next;
        });
      }
    }, { root: null, rootMargin: '200px', threshold: 0 });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore]);

  const applyFilters = () => {
    let filtered = [...stocks];

    // Search filter
    if (searchTerm) {
      // Debounce tracking for search term could be better, but simple event here for applied filter
      if (searchTerm.length > 2) {
        // Note: Moving analytics out of render/filter loop is better, but this is inside applyFilters which runs on effect.
        // To avoid spam, we might only check if it changed significantly, or rely on the effect debounce if it exists.
        // For now, let's track distinct search actions if possible, or bind to the input change handler instead.
      }
      filtered = filtered.filter(stock =>
        stock.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stock.sector?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stock.industry?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Primary type filter
    if (filters.primaryType) {
      filtered = filtered.filter(stock => extractPrimaryType(stock) === filters.primaryType);
    }

    // Market cap filter
    if (filters.minMarketCap || filters.maxMarketCap) {
      const [minStr, maxStr] = (filters.minMarketCap || filters.maxMarketCap).split('-');
      const min = Number(minStr);
      const max = Number(maxStr);
      if (Number.isFinite(min) && Number.isFinite(max)) {
        filtered = filtered.filter(stock => {
          const mc = numericMarketCap(stock);
          return mc >= min && mc < max; // inclusive lower, exclusive upper
        });
      }
    }

    // Sector handled server-side

    // Grade filter
    if (filters.grade) {
      filtered = filtered.filter(stock => extractGradeLetter(stock) === filters.grade);
    }

    // Quality flag filter
    if (filters.qualityFlag) {
      filtered = filtered.filter(stock => extractQualityFlags(stock).includes(filters.qualityFlag));
    }

    // Maintain server order: sort by overallScore desc after client filters
    filtered.sort((a, b) => (b.overallScore ?? 0) - (a.overallScore ?? 0));
    setFilteredStocks(filtered);
  };

  const formatMarketCap = (value: number): string => {
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${value.toLocaleString()}`;
  };

  const getGradeColor = (grade: string): string => {
    const isDark = theme === 'dark';
    switch (grade) {
      case 'A':
        return isDark ? 'bg-emerald-900/40 text-emerald-200 border-emerald-700' : 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'B':
        return isDark ? 'bg-sky-900/40 text-sky-200 border-sky-700' : 'bg-sky-100 text-sky-800 border-sky-300';
      case 'C':
        return isDark ? 'bg-amber-900/40 text-amber-200 border-amber-700' : 'bg-amber-100 text-amber-900 border-amber-300';
      case 'D':
        return isDark ? 'bg-orange-900/40 text-orange-200 border-orange-700' : 'bg-orange-100 text-orange-900 border-orange-300';
      case 'F':
        return isDark ? 'bg-rose-900/40 text-rose-200 border-rose-700' : 'bg-rose-100 text-rose-900 border-rose-300';
      default:
        return isDark ? 'bg-theme-tertiary text-gray-200 border-theme-tertiary' : 'bg-gray-100 text-gray-900 border-gray-300';
    }
  };

  const getPrimaryTypeIcon = (type: string) => {
    switch (type) {
      case 'High Growth': return <TrendingUp className="w-4 h-4" />;
      case 'Value': return <DollarSign className="w-4 h-4" />;
      case 'Quality': return <Shield className="w-4 h-4" />;
      case 'Turnaround': return <BarChart3 className="w-4 h-4" />;
      case 'At Risk': return <AlertTriangle className="w-4 h-4" />;
      case 'Dividend': return <DollarSign className="w-4 h-4" />;
      default: return <BarChart3 className="w-4 h-4" />;
    }
  };

  const getPrimaryTypeColor = (type: string): string => {
    const isDark = theme === 'dark';
    switch (type) {
      case 'High Growth': return isDark ? 'bg-purple-900/40 text-purple-200 border-purple-700' : 'bg-purple-100 text-purple-800 border-purple-300';
      case 'Value': return isDark ? 'bg-blue-900/40 text-blue-200 border-blue-700' : 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Quality': return isDark ? 'bg-green-900/40 text-green-200 border-green-700' : 'bg-green-100 text-green-800 border-green-300';
      case 'Turnaround': return isDark ? 'bg-yellow-900/40 text-yellow-200 border-yellow-700' : 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'At Risk': return isDark ? 'bg-red-900/40 text-red-200 border-red-700' : 'bg-red-100 text-red-800 border-red-300';
      case 'Dividend': return isDark ? 'bg-emerald-900/40 text-emerald-200 border-emerald-700' : 'bg-emerald-100 text-emerald-800 border-emerald-300';
      default: return isDark ? 'bg-theme-tertiary text-gray-200 border-theme-tertiary' : 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const resetFilters = () => {
    setFilters({
      primaryType: '',
      minMarketCap: '',
      maxMarketCap: '',
      grade: '',
      qualityFlag: ''
    });
    setSearchTerm('');
  };

  // Helper function to get tooltip text for flags and warnings
  const getTooltipText = (text: string): string => {
    const tooltips: Record<string, string> = {
      // Quality Flags
      'Profitable': 'The company makes more money than it spends.',
      'Strong Balance Sheet': "It's financially sturdy—reasonable debt and good ability to pay bills.",
      'Consistent Revenue Growth': 'Sales have been rising steadily over time.',
      'Strong Revenue Growth': 'Sales are growing quickly right now.',
      'Improving Revenue': 'Sales are starting to trend up after a slower period.',
      'Stable Margins': 'The company keeps a steady percentage of profit from each dollar of sales.',
      'Improving Margins': "It's getting better at turning sales into profit.",
      'Efficient Operations': 'It uses its assets well to generate sales (runs a tight ship).',
      'Value Creator': 'Management invests money in ways that earn clearly more than their cost (creates shareholder value).',
      'Deep Value': 'Shares look very cheap compared with typical levels for this industry.',
      'Low Valuation': 'Shares look cheaper than usual.',
      'Reasonable Valuation': 'Price looks fair—not especially cheap or expensive.',
      'Pays Dividend': 'It pays cash to shareholders.',
      'Dividend Payer': 'It pays a dividend at a typical/sensible level.',
      'Sustainable High Dividend': 'A higher dividend that still looks maintainable.',
      'Profitability Inflection': 'Likely to switch from losing money to making money soon.',
      'Growth Recovery Expected': 'Sales or earnings slowed recently but are projected to recover.',
      'Yield ≥ Floor': 'Free cash flow income is solid relative to price.',
      'P/FCF ≤ Ceiling': 'Price vs. free cash flow is attractive.',
      'FCFPS CAGR ≥ 8%': 'Free cash flow per share has grown well over the last few years.',
      'Low FCFPS Volatility': 'Free cash flow per share is steady, not jumpy.',
      // Warnings
      'Declining Revenue': 'Sales fell in most recent quarters.',
      'Severe Recent Decline': 'Sales dropped sharply very recently.',
      'Unprofitable': 'Currently loses money.',
      'Weak Balance Sheet': 'Debt/liquidity look risky.',
      'Thin or Below-Threshold Margins': "Doesn't keep enough profit from sales.",
      'Weak Recent Revenue Trend': "Sales aren't improving lately.",
      'Potential Value Trap': 'Looks cheap, but weak fundamentals suggest it might deserve the low price.',
      'Dividend At Risk': 'Dividend may be too high to sustain (payout looks stretched).',
      'Premium Valuation vs Sector': 'Priced above peers in its sector.',
    };

    // Handle partial matches for valuation warnings
    if (text.includes('Extreme Overvaluation Risk')) return 'Price is far above peers based on earnings.';
    if (text.includes('Overvaluation Risk (P/S)')) return 'Price is high compared with sales, especially worrying if growth is modest.';
    if (text.includes('P/S ratio')) return 'Price vs. sales looks stretched even without sector data.';

    return tooltips[text] || text;
  };

  // sectorOptions from metadata API

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length + (serverSector ? 1 : 0);

  const isDark = theme === 'dark';

  const seoMeta = getToolSEOMeta('screener');

  return (
    <div className={`min-h-screen bg-gradient-to-br ${isDark ? 'from-gray-900 to-gray-800' : 'from-gray-50 to-gray-100'}`}>
      {/* SEO Meta Tags */}
      <SEOMeta
        title={seoMeta.title}
        description={seoMeta.description}
        keywords={seoMeta.keywords}
      />

      {/* Compact spacer (header removed per design) */}
      <div className="h-2" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Quick Picks row with count and filter toggle */}
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Top At Risk', kind: 'AT_RISK' },
                { label: 'Top Growth', kind: 'GROWTH' },
                { label: 'Top Value', kind: 'VALUE' },
                { label: 'Top Dividend', kind: 'DIVIDEND' },
                { label: 'Top Turnarounds', kind: 'TURNAROUND' },
              ].map(p => {
                const isActive = mode === 'topByCategory' && category === p.kind;
                return (
                  <button
                    key={p.kind}
                    onClick={() => applyPreset(p.kind as any)}
                    className={`px-3 py-1.5 text-sm rounded-full border btn ${isActive ? 'active' : `${isDark ? 'border-theme-tertiary bg-theme-tertiary text-gray-200 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'}`}`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm btn hover:bg-yellow-700 text-theme-primary rounded-md transition-colors"
              >
                <Filter className="w-4 h-4" />
                {showFilters ? 'Hide' : 'Show'} Filters
                {activeFilterCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-white text-blue-600 rounded-full text-[10px] font-bold">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Filters Panel (horizontal row) */}
        {showFilters && (
          <div className={`mb-4 ${isDark ? 'bg-theme-secondary border-theme-tertiary' : 'bg-white border-gray-200'} rounded-lg shadow-md p-3 border`}>
            <div className="flex flex-wrap items-end gap-4">
              {/* Symbol Search */}
              <div className="min-w-[280px]">
                <label className={`block text-sm font-medium ${isDark ? 'text-theme-secondary' : 'text-gray-700'} mb-1`}>
                  Search by Symbol
                </label>
                <form onSubmit={handleSymbolSearch} className="flex gap-2">
                  <input
                    type="text"
                    value={symbolSearch}
                    onChange={(e) => setSymbolSearch(e.target.value.toUpperCase())}
                    placeholder="e.g. AAPL"
                    className={`flex-1 px-3 py-2 border ${isDark ? 'border-theme-tertiary bg-theme-tertiary text-theme-primary placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    disabled={searchingSymbol}
                  />
                  <button
                    type="submit"
                    disabled={searchingSymbol || !symbolSearch.trim()}
                    className="px-4 py-2 btn hover:bg-yellow-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-theme-primary rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    {searchingSymbol ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      'Search'
                    )}
                  </button>
                  {symbolSearchMode && (
                    <button
                      type="button"
                      onClick={clearSymbolSearch}
                      className={`px-4 py-2 ${isDark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'} rounded-lg font-medium transition-colors`}
                    >
                      Clear
                    </button>
                  )}
                </form>
              </div>

              <div className="min-w-[220px]">
                <label className={`block text-sm font-medium ${isDark ? 'text-theme-secondary' : 'text-gray-700'} mb-1`}>Sector</label>
                <select
                  value={serverSector}
                  onChange={(e) => { setServerSector(e.target.value); setPage(0); }}
                  disabled={symbolSearchMode}
                  className={`w-full px-3 py-2 border ${isDark ? 'border-theme-tertiary bg-theme-tertiary text-theme-primary' : 'border-gray-300 bg-white text-gray-900'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <option value="">All Sectors</option>
                  {sectorOptions.map(sector => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-[220px]">
                <label className={`block text-sm font-medium ${isDark ? 'text-theme-secondary' : 'text-gray-700'} mb-1`}>Market Cap</label>
                <select
                  value={filters.minMarketCap}
                  onChange={(e) => setFilters({ ...filters, minMarketCap: e.target.value, maxMarketCap: e.target.value })}
                  disabled={symbolSearchMode}
                  className={`w-full px-3 py-2 border ${isDark ? 'border-theme-tertiary bg-theme-tertiary text-theme-primary' : 'border-gray-300 bg-white text-gray-900'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {marketCapOptions.map(range => (
                    <option key={range.value} value={range.value}>{range.label}</option>
                  ))}
                </select>
              </div>
              <div className={`ml-auto text-sm ${isDark ? 'text-theme-muted' : 'text-gray-600'}`}>
                <span className={`text-base font-bold ${isDark ? 'text-theme-primary' : 'text-gray-900'}`}>{(totalCount ?? 0).toLocaleString()}</span>
                <span className="ml-1">{symbolSearchMode ? 'result' : 'stocks found'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Infinite scroll status */}
        {loadingMore && (
          <div className={`mb-3 text-sm ${isDark ? 'text-theme-muted' : 'text-gray-500'}`}>Loading more...</div>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className={`ml-3 ${isDark ? 'text-theme-muted' : 'text-gray-600'}`}>Loading stocks...</span>
          </div>
        ) : filteredStocks.length === 0 ? (
          <div className={`text-center py-20 ${isDark ? 'bg-theme-secondary border-theme-tertiary' : 'bg-white border-gray-200'} rounded-lg border`}>
            <Filter className="w-16 h-16 text-theme-muted mx-auto mb-4" />
            <h3 className={`text-xl font-semibold ${isDark ? 'text-theme-primary' : 'text-gray-900'} mb-2`}>
              No stocks found
            </h3>
            <p className={`${isDark ? 'text-theme-muted' : 'text-gray-600'} mb-4`}>Try adjusting your filters</p>
            <button
              onClick={resetFilters}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-theme-primary rounded-lg font-medium transition-colors"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className={`${isDark ? 'bg-theme-secondary border-theme-tertiary' : 'bg-white border-gray-200'} rounded-lg border overflow-x-auto`}>
            <table className="min-w-full text-sm">
              <thead className={`${isDark ? 'bg-theme-tertiary text-gray-200' : 'bg-gray-50 text-gray-700'}`}>
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Symbol</th>
                  <th className="px-4 py-2 text-left font-semibold">Type</th>
                  <th className="px-4 py-2 text-left font-semibold">Industry</th>
                  <th className="px-4 py-2 text-right font-semibold">Market Cap</th>
                  <th className="px-4 py-2 text-center font-semibold">Grade</th>
                  <th className="px-4 py-2 text-center font-semibold">Flags</th>
                  <th className="px-4 py-2 text-right font-semibold">Score</th>
                </tr>
              </thead>
              <tbody className={`${isDark ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
                {filteredStocks.map((stock) => (
                  <>
                    <tr
                      key={stock.id}
                      onClick={() => navigate(`/${stock.symbol}/summary`)}
                      className={`${isDark ? 'hover:bg-theme-tertiary' : 'hover:bg-gray-50'} cursor-pointer`}
                    >
                      <td className={`px-4 py-2 whitespace-nowrap font-semibold ${isDark ? 'active-text' : 'text-theme-primary'}`}>{stock.symbol}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{extractPrimaryType(stock)}</td>
                      <td className="px-4 py-2 truncate max-w-[320px]">{stock.industry}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">{formatMarketCap(numericMarketCap(stock))}</td>
                      <td className="px-4 py-2 text-center whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-bold border ${getGradeColor(extractGradeLetter(stock))}`}>
                          {extractGradeLetter(stock) || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center whitespace-nowrap" onClick={(e) => { e.stopPropagation(); toggleExpand(stock.id); }}>
                        {extractQualityFlags(stock).length > 0 && (
                          <span className={`inline-block mx-1 px-2 py-0.5 text-xs rounded ${isDark ? 'bg-green-900/30 text-green-300 border-green-700' : 'bg-green-100 text-green-700 border-green-300'} border`}>Q {extractQualityFlags(stock).length}</span>
                        )}
                        {Array.isArray(stock.warnings) && stock.warnings.length > 0 && (
                          <span className={`inline-block mx-1 px-2 py-0.5 text-xs rounded ${isDark ? 'bg-red-900/30 text-red-300 border-red-700' : 'bg-red-100 text-red-700 border-red-300'} border`}>W {stock.warnings.length}</span>
                        )}
                        <ChevronDown className={`inline w-4 h-4 ml-1 transition-transform ${expandedIds.has(stock.id) ? 'rotate-180' : ''}`} />
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap font-semibold">{Number.isFinite(stock.overallScore) ? stock.overallScore.toFixed(1) : '-'}</td>
                    </tr>
                    {expandedIds.has(stock.id) && (
                      <tr>
                        <td colSpan={7} className={`px-4 py-3 ${isDark ? 'bg-theme-primary' : 'bg-gray-50'}`}>
                          <div className="flex flex-col md:flex-row md:items-start gap-6">
                            {extractQualityFlags(stock).length > 0 && (
                              <div className="min-w-[240px]">
                                <div className={`text-xs font-semibold ${isDark ? 'text-theme-muted' : 'text-gray-500'} uppercase mb-2`}>Quality Flags</div>
                                <div className="flex flex-wrap gap-2">
                                  {extractQualityFlags(stock).map((flag, idx) => (
                                    <span
                                      key={idx}
                                      className={`px-2 py-1 ${isDark ? 'bg-green-900/30 text-green-400 border-green-700' : 'bg-green-100 text-green-800 border-green-300'} text-xs rounded-md border cursor-help`}
                                      title={getTooltipText(flag)}
                                    >
                                      {flag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {Array.isArray(stock.warnings) && stock.warnings.length > 0 && (
                              <div className="min-w-[240px]">
                                <div className={`text-xs font-semibold ${isDark ? 'text-theme-muted' : 'text-gray-500'} uppercase mb-2`}>Warnings</div>
                                <div className="flex flex-wrap gap-2">
                                  {stock.warnings.map((w, idx) => (
                                    <span
                                      key={idx}
                                      className={`px-2 py-1 ${isDark ? 'bg-red-900/30 text-red-400 border-red-700' : 'bg-red-100 text-red-800 border-red-300'} text-xs rounded-md border cursor-help`}
                                      title={getTooltipText(w)}
                                    >
                                      {w}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Sentinel for infinite scroll */}
        <div ref={sentinelRef} className="h-8" />
      </div>
    </div>
  );
};

export default Screener;

